<template>
  <div class="col-span-12 lg:col-span-4 shadow-md p-5">
    <div
      class="events-header bg-gradient-to-r from-blue-50 to-indigo-50 p-4 border-b border-gray-100 rounded-t-lg"
    >
      <h2 class="text-xl font-bold text-gray-800 flex items-center">
        <i class="pi pi-calendar-plus mr-2 text-indigo-500"></i>
        Upcoming Events
      </h2>
    </div>

    <div class="p-4">
      <div v-if="!events || events.length === 0" class="text-center py-6 text-gray-500">
        <i class="pi pi-clock text-3xl mb-2"></i>
        <p v-if="events.length === 0">No available events...</p>
        <p v-else>Loading events...</p>
      </div>

      <div v-else class="upcoming-events">
        <div
          v-for="event in events"
          :key="event.pid"
          class="event-item"
          @click="toggleEventDetails(event.pid)"
          :class="{
            active: expandedEventId === event.pid,
            'global-event': event.isDivisionWide,
            'school-event': !event.isDivisionWide
          }"
        >
          <!-- Event date display based on duration type -->
          <div
            class="event-date"
            :class="{
              'multi-day': event.dateInfo.displayType !== 'single',
              'global-date': event.isDivisionWide,
              'school-date': !event.isDivisionWide
            }"
          >
            <!-- Event Type Icon Badge -->
            <div class="event-type-badge" :class="event.isDivisionWide ? 'global-badge' : 'school-badge'">
              <i :class="event.isDivisionWide ? 'pi pi-globe' : 'pi pi-building'" class="text-xs"></i>
            </div>

            <!-- Single-day event display -->
            <template v-if="event.dateInfo.displayType === 'single'">
              <span class="event-month">{{ event.dateInfo.month }}</span>
              <span class="event-day">{{ event.dateInfo.day }}</span>
            </template>

            <!-- Multi-day, same month display -->
            <template v-else-if="event.dateInfo.displayType === 'multi-same-month'">
              <span class="event-month">{{ event.dateInfo.month }}</span>
              <div class="event-days-range">
                <span class="from-day">{{ event.dateInfo.fromDay }}</span>
                <span class="range-separator">-</span>
                <span class="to-day">{{ event.dateInfo.toDay }}</span>
              </div>
            </template>

            <!-- Multi-day, different months display -->
            <template v-else>
              <div class="date-range-compact">
                <div class="from-date">
                  <span class="from-month">{{ event.dateInfo.fromMonth }}</span>
                  <span class="from-day">{{ event.dateInfo.fromDay }}</span>
                </div>
                <span class="range-separator">→</span>
                <div class="to-date">
                  <span class="to-month">{{ event.dateInfo.toMonth }}</span>
                  <span class="to-day">{{ event.dateInfo.toDay }}</span>
                </div>
              </div>
            </template>
          </div>

          <div class="event-details">
            <h3 class="event-title">
              {{ event.title }}
              <span v-if="event.isDivisionWide" class="event-scope-badge global">
                <i class="pi pi-globe mr-1"></i>
                Division-Wide
              </span>
              <span v-else class="event-scope-badge school">
                <i class="pi pi-building mr-1"></i>
                School Level
              </span>
              <i
                class="pi ml-auto"
                :class="expandedEventId === event.pid ? 'pi-chevron-up' : 'pi-chevron-down'"
                style="font-size: 0.75rem"
              ></i>
            </h3>
            <p
              v-if="event.dateInfo.displayType !== 'single'"
              class="event-date-display text-xs text-blue-600 font-medium mb-1"
            >
              <i class="pi pi-calendar mr-1"></i> {{ event.dateInfo.dateDisplay }}
            </p>

            <!-- Event summary always shown -->
            <p class="event-time"><i class="pi pi-clock mr-1"></i> {{ event.time }}</p>
            <p class="event-location">
              <i class="pi mr-1" :class="[event.locationIcon, event.locationColor]"></i>
              <span :class="event.locationColor">{{ event.location }}</span>
            </p>
            <Tag :value="event.type" :severity="event.severity" class="mt-2" />

            <!-- Expanded details section -->
            <div v-if="expandedEventId === event.pid" class="event-expanded-details">
              <div class="event-description mt-3 p-3 bg-gray-50 rounded-lg">
                <h4 class="font-semibold mb-2">Event Details</h4>
                <p class="text-sm text-gray-700 mb-2">
                  {{ event.description || 'No detailed description available.' }}
                </p>

                <!-- Additional details can be added here -->
                <div class="event-organizer mt-2" v-if="event.organizer">
                  <span class="text-xs font-medium text-gray-500">Organized by:</span>
                  <span class="text-sm ml-1">{{ event.organizer }}</span>
                </div>

                <div class="event-category mt-2" v-if="event.category">
                  <span class="text-xs font-medium text-gray-500">Category:</span>
                  <span class="text-sm ml-1">{{ event.category }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-6 text-center">
        <Button
          label="View All Events"
          icon="pi pi-calendar"
          class="p-button-outlined p-button-rounded"
          @click="openAllEventsDialog"
        />
      </div>
    </div>

    <!-- All Events Dialog -->
    <Dialog
      v-model:visible="allEventsDialogVisible"
      modal
      header="All Events"
      :style="{ width: '90vw', maxWidth: '1200px' }"
      :maximizable="true"
      class="events-dialog"
    >
      <div class="event-search-box mb-4">
        <div class="flex flex-row gap-2">
          <span class="p-input-icon-left flex-1">
            <InputText v-model="searchQuery" placeholder="Search events..." class="w-full" />
          </span>
          <div class="flex items-center gap-2">
            <span class="text-sm text-gray-600">Filter:</span>
            <Button
              :label="`Division-Wide (${divisionWideCount})`"
              icon="pi pi-globe"
              :severity="filterType === 'division' ? 'info' : 'secondary'"
              :outlined="filterType !== 'division'"
              size="small"
              @click="filterType = filterType === 'division' ? 'all' : 'division'"
            />
            <Button
              :label="`School Level (${schoolLevelCount})`"
              icon="pi pi-building"
              :severity="filterType === 'school' ? 'success' : 'secondary'"
              :outlined="filterType !== 'school'"
              size="small"
              @click="filterType = filterType === 'school' ? 'all' : 'school'"
            />
          </div>
        </div>
      </div>

      <div class="events-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          v-for="event in filteredAllEvents"
          :key="event.pid"
          class="event-card shadow-sm border border-gray-100 rounded-lg overflow-hidden"
          :class="{
            'global-event-card': event.isDivisionWide,
            'school-event-card': !event.isDivisionWide
          }"
        >
          <div class="flex">
            <!-- Left side: Date -->
            <div
              class="event-card-date"
              :class="{
                'multi-day': event.dateInfo.displayType !== 'single',
                'global-date': event.isDivisionWide,
                'school-date': !event.isDivisionWide
              }"
            >
              <!-- Event Type Icon Badge -->
              <!-- <div class="event-type-badge card-badge" :class="event.isDivisionWide ? 'global-badge' : 'school-badge'">
                <i :class="event.isDivisionWide ? 'pi pi-globe' : 'pi pi-building'" class="text-xs"></i>
              </div> -->

              <!-- Single-day event display -->
              <template v-if="event.dateInfo.displayType === 'single'">
                <span class="event-month">{{ event.dateInfo.month }}</span>
                <span class="event-day">{{ event.dateInfo.day }}</span>
              </template>

              <!-- Multi-day, same month display -->
              <template v-else-if="event.dateInfo.displayType === 'multi-same-month'">
                <span class="event-month">{{ event.dateInfo.month }}</span>
                <div class="event-days-range">
                  <span class="from-day">{{ event.dateInfo.fromDay }}</span>
                  <span class="range-separator">-</span>
                  <span class="to-day">{{ event.dateInfo.toDay }}</span>
                </div>
              </template>

              <!-- Multi-day, different months display -->
              <template v-else>
                <div class="date-range-compact">
                  <div class="from-date">
                    <span class="from-month">{{ event.dateInfo.fromMonth }}</span>
                    <span class="from-day">{{ event.dateInfo.fromDay }}</span>
                  </div>
                  <span class="range-separator">→</span>
                  <div class="to-date">
                    <span class="to-month">{{ event.dateInfo.toMonth }}</span>
                    <span class="to-day">{{ event.dateInfo.toDay }}</span>
                  </div>
                </div>
              </template>
            </div>

            <!-- Right side: Event details -->
            <div class="event-card-content p-4 flex-1">
              <div class="flex items-start gap-2 mb-2">
                <h3 class="text-lg font-semibold text-gray-800 flex-1 leading-tight">{{ event.title }}</h3>
                <span v-if="event.isDivisionWide" class="event-scope-badge-small global flex-shrink-0">
                  <i class="pi pi-globe"></i>
                </span>
                <span v-else class="event-scope-badge-small school flex-shrink-0">
                  <i class="pi pi-building"></i>
                </span>
              </div>

              <p
                v-if="event.dateInfo.displayType !== 'single'"
                class="text-xs text-blue-600 font-medium mb-1"
              >
                <i class="pi pi-calendar mr-1"></i> {{ event.dateInfo.dateDisplay }}
              </p>

              <p class="text-sm text-gray-600 mb-1">
                <i class="pi pi-clock mr-1"></i> {{ event.time }}
              </p>

              <p class="text-sm mb-2" :class="event.locationColor">
                <i class="pi mr-1" :class="event.locationIcon"></i> {{ event.location }}
              </p>

              <Tag :value="event.type" :severity="event.severity" class="mb-3" />

              <p class="text-sm text-gray-700 mt-2 line-clamp-2">
                {{ event.description || 'No detailed description available.' }}
              </p>

              <div class="mt-4">
                <Button
                  label="View Details"
                  icon="pi pi-info-circle"
                  class="p-button-sm p-button-outlined"
                  @click="viewEventDetails(event)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Event Details Sidebar -->
      <Sidebar
        v-model:visible="eventDetailsSidebarVisible"
        position="right"
        :style="{ width: '400px' }"
        class="p-0"
      >
        <div v-if="selectedEvent" class="h-full flex flex-col">
          <div
            class="event-details-header p-4 border-b border-gray-200"
            :class="selectedEvent.isDivisionWide ? 'bg-gradient-to-r from-purple-50 to-indigo-50' : 'bg-gradient-to-r from-green-50 to-emerald-50'"
          >
            <div class="flex items-start justify-between mb-2">
              <h2 class="text-xl font-bold text-gray-800 flex-1">{{ selectedEvent.title }}</h2>
              <span v-if="selectedEvent.isDivisionWide" class="event-scope-badge global">
                <i class="pi pi-globe mr-1"></i>
                Division
              </span>
              <span v-else class="event-scope-badge school">
                <i class="pi pi-building mr-1"></i>
                School
              </span>
            </div>
            <p class="text-sm text-blue-600 mt-1">
              <i class="pi pi-calendar mr-1"></i> {{ selectedEvent.dateInfo.dateDisplay }}
            </p>
          </div>

          <div class="flex-1 p-4 overflow-y-auto">
            <div class="event-info-group mb-4">
              <h3 class="text-sm font-semibold text-gray-500 mb-1">Time</h3>
              <p class="text-md">{{ selectedEvent.time }}</p>
            </div>

            <div class="event-info-group mb-4">
              <h3 class="text-sm font-semibold text-gray-500 mb-1">Location</h3>
              <p class="text-md" :class="selectedEvent.locationColor">
                <i class="pi mr-1" :class="selectedEvent.locationIcon"></i>
                {{ selectedEvent.location }}
              </p>
            </div>

            <div class="event-info-group mb-4">
              <h3 class="text-sm font-semibold text-gray-500 mb-1">Category</h3>
              <Tag :value="selectedEvent.type" :severity="selectedEvent.severity" />
            </div>

            <div class="event-info-group mb-4" v-if="selectedEvent.organizer">
              <h3 class="text-sm font-semibold text-gray-500 mb-1">Organized by</h3>
              <p class="text-md">{{ selectedEvent.organizer }}</p>
            </div>

            <div class="event-info-group">
              <h3 class="text-sm font-semibold text-gray-500 mb-1">Description</h3>
              <p class="text-md whitespace-pre-line">
                {{ selectedEvent.description || 'No detailed description available.' }}
              </p>
            </div>
          </div>

          <div class="p-4 border-t border-gray-200">
            <div class="flex justify-between"></div>
          </div>
        </div>
      </Sidebar>

      <template #footer>
        <div class="flex justify-between">
          <div class="text-sm text-gray-500">
            Total: {{ filteredAllEvents.length }} events
            <span class="ml-2">(Division: {{ divisionWideCount }}, School: {{ schoolLevelCount }})</span>
          </div>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useEventStore } from '@/store/admin/EventStore'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/auth/auth';

const props = defineProps({
  eventlimit: {
    type: Number,
    default: 3,
  },
})

const authStore = useAuthStore()
const eventStore = useEventStore()
const { eventx } = storeToRefs(eventStore)
eventStore.getEventPID(authStore.school_id)

// State for tracking expanded event
const expandedEventId = ref(null)

// Dialog state
const allEventsDialogVisible = ref(false)
const searchQuery = ref('')
const selectedEvent = ref(null)
const eventDetailsSidebarVisible = ref(false)
const filterType = ref('all') // 'all', 'division', 'school'

// Toggle event details expansion
function toggleEventDetails(eventId) {
  if (expandedEventId.value === eventId) {
    expandedEventId.value = null
  } else {
    expandedEventId.value = eventId
  }
}

// Open the all events dialog
function openAllEventsDialog() {
  allEventsDialogVisible.value = true
}

// Close the all events dialog
function closeAllEventsDialog() {
  allEventsDialogVisible.value = false
}

// View event details in sidebar
function viewEventDetails(event) {
  selectedEvent.value = event
  eventDetailsSidebarVisible.value = true
}

// Transform raw event data into display format
const events = computed(() => {
  const eventData = eventx.value?.data || eventx.value

  if (!eventData || !Array.isArray(eventData)) {
    return []
  }

  const transformedEvents = eventData.map((event) => {
    const dateRange = formatDateRange(event.event_from_date, event.event_to_date)
    const isDivisionWide = !event.school || event.school === null

    return {
      pid: event.pid,
      dateInfo: dateRange,
      title: event.name,
      description: event.description,
      time: formatEventTime(event.event_from_time, event.event_to_time),
      location: event.school?.name || 'Entire Division',
      isDivisionWide: isDivisionWide,
      locationIcon: isDivisionWide ? 'pi-globe' : 'pi-map-marker',
      locationColor: isDivisionWide ? 'text-purple-600' : 'text-green-600',
      type: event.event_type.description,
      severity: getSeverity(event.event_type.type),
      organizer: event.organizer || 'Admin',
      category: event.category || event.event_type.description,
    }
  })

  transformedEvents.sort((a, b) => {
    const dateA = new Date(a.dateInfo.fullFromDate)
    const dateB = new Date(b.dateInfo.fullFromDate)
    return dateA - dateB
  })

  return props.eventlimit > 0 ? transformedEvents.slice(0, props.eventlimit) : transformedEvents
})

// Get all events (unfiltered)
const allEvents = computed(() => {
  const eventData = eventx.value?.data || eventx.value

  if (!eventData || !Array.isArray(eventData)) {
    return []
  }

  const transformedEvents = eventData.map((event) => {
    const dateRange = formatDateRange(event.event_from_date, event.event_to_date)
    const isDivisionWide = !event.school || event.school === null

    return {
      pid: event.pid,
      dateInfo: dateRange,
      title: event.name,
      description: event.description,
      time: formatEventTime(event.event_from_time, event.event_to_time),
      location: event.school?.name || 'Entire Division',
      isDivisionWide: isDivisionWide,
      locationIcon: isDivisionWide ? 'pi-globe' : 'pi-map-marker',
      locationColor: isDivisionWide ? 'text-purple-600' : 'text-green-600',
      type: event.event_type.description,
      severity: getSeverity(event.event_type.type),
      organizer: event.organizer || 'Admin',
      category: event.category || event.event_type.description,
    }
  })

  transformedEvents.sort((a, b) => {
    const dateA = new Date(a.dateInfo.fullFromDate)
    const dateB = new Date(b.dateInfo.fullFromDate)
    return dateA - dateB
  })

  return transformedEvents
})

// Count division-wide and school-level events
const divisionWideCount = computed(() => {
  return allEvents.value.filter(event => event.isDivisionWide).length
})

const schoolLevelCount = computed(() => {
  return allEvents.value.filter(event => !event.isDivisionWide).length
})

// Filtered events based on search query and filter type
const filteredAllEvents = computed(() => {
  let filtered = allEvents.value

  // Apply type filter
  if (filterType.value === 'division') {
    filtered = filtered.filter(event => event.isDivisionWide)
  } else if (filterType.value === 'school') {
    filtered = filtered.filter(event => !event.isDivisionWide)
  }

  // Apply search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(
      (event) =>
        event.title.toLowerCase().includes(query) ||
        event.description?.toLowerCase().includes(query) ||
        event.location.toLowerCase().includes(query) ||
        event.type.toLowerCase().includes(query) ||
        event.dateInfo.dateDisplay.toLowerCase().includes(query),
    )
  }

  return filtered
})

// Helper function to format date ranges
function formatDateRange(fromDateStr, toDateStr) {
  const from = new Date(fromDateStr)
  const to = new Date(toDateStr)

  const sameDay =
    from.getDate() === to.getDate() &&
    from.getMonth() === to.getMonth() &&
    from.getFullYear() === to.getFullYear()

  const sameMonthYear = from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear()

  if (sameDay) {
    return {
      displayType: 'single',
      month: from.toLocaleString('en-US', { month: 'short' }),
      day: from.getDate(),
      dateDisplay: from.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      fullFromDate: fromDateStr,
      fullToDate: toDateStr,
    }
  }

  if (sameMonthYear) {
    return {
      displayType: 'multi-same-month',
      month: from.toLocaleString('en-US', { month: 'short' }),
      fromDay: from.getDate(),
      toDay: to.getDate(),
      dateDisplay: `${from.getDate()} - ${to.getDate()} ${from.toLocaleString('en-US', { month: 'short' })}`,
      fullFromDate: fromDateStr,
      fullToDate: toDateStr,
    }
  }

  return {
    displayType: 'multi-diff-month',
    fromMonth: from.toLocaleString('en-US', { month: 'short' }),
    fromDay: from.getDate(),
    toMonth: to.toLocaleString('en-US', { month: 'short' }),
    toDay: to.getDate(),
    dateDisplay: `${from.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${to.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
    fullFromDate: fromDateStr,
    fullToDate: toDateStr,
  }
}

function formatEventTime(startTime, endTime) {
  const formatTime = (timeStr) => {
    if (!timeStr) return ''
    const [hours, minutes] = timeStr.split(':')
    const hour = parseInt(hours, 10)
    const period = hour >= 12 ? 'PM' : 'AM'
    const hour12 = hour % 12 || 12
    return `${hour12}:${minutes.padStart(2, '0')} ${period}`
  }

  return `${formatTime(startTime)} - ${formatTime(endTime)}`
}

function getSeverity(eventType) {
  const typeMap = {
    regular_holiday: 'success',
    special_working_holiday: 'warning',
    special_non_working_holiday: 'danger',
  }
  return typeMap[eventType] || 'info'
}

defineExpose({
  openAllEventsDialog,
  closeAllEventsDialog,
  events,
  allEvents
})
</script>

<style scoped>
/* Event Type Badge */
.event-type-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 10;
}

.global-badge {
  background: linear-gradient(135deg, #a855f7, #7c3aed);
  color: white;
}

.school-badge {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.card-badge {
  top: 8px;
  right: 8px;
}

/* Event Scope Badge */
.event-scope-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.event-scope-badge.global {
  background: linear-gradient(135deg, #ede9fe, #ddd6fe);
  color: #7c3aed;
  border: 1px solid #c4b5fd;
}

.event-scope-badge.school {
  background: linear-gradient(135deg, #d1fae5, #a7f3d0);
  color: #059669;
  border: 1px solid #6ee7b7;
}

.event-scope-badge-small {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  flex-shrink: 0;
}

.event-scope-badge-small.global {
  background: linear-gradient(135deg, #a855f7, #7c3aed);
  color: white;
}

.event-scope-badge-small.school {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

/* Event Item Styling */
.event-item {
  display: flex;
  margin-bottom: .5rem;
  gap: 1rem;
  padding: 1rem 0.75rem;
  border-bottom: 1px solid #f3f4f6;
  transition: all 0.2s ease;
  border-radius: 0.5rem;
  cursor: pointer;
  position: relative;
}

.event-item.global-event {
  border-left: 3px solid #a855f7;
}

.event-item.school-event {
  border-left: 3px solid #10b981;
}

.event-item:hover {
  background-color: #f9fafb;
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.event-item.active.global-event {
  background: linear-gradient(135deg, #faf5ff, #f3e8ff);
  border-left: 3px solid #7c3aed;
}

.event-item.active.school-event {
  background: linear-gradient(135deg, #ecfdf5, #d1fae5);
  border-left: 3px solid #059669;
}

.event-item:last-child {
  border-bottom: none;
}

/* Event Date Styling */
.event-date,
.event-card-date {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 60px;
  height: 70px;
  border-radius: 0.75rem;
  padding: 0.5rem;
  flex-shrink: 0;
  position: relative;
}

.event-date.global-date {
  background: linear-gradient(135deg, #faf5ff, #f3e8ff);
  border-left: 3px solid #a855f7;
}

.event-date.school-date {
  background: linear-gradient(135deg, #ecfdf5, #d1fae5);
  border-left: 3px solid #10b981;
}

.event-card-date {
  width: 80px;
  height: 100%;
  border-radius: 0;
  border-left: none;
  border-right: 1px solid #e5e7eb;
}

.event-card-date.global-date {
  background: linear-gradient(135deg, #faf5ff, #f3e8ff);
  border-right: 3px solid #a855f7;
}

.event-card-date.school-date {
  background: linear-gradient(135deg, #ecfdf5, #d1fae5);
  border-right: 3px solid #10b981;
}

/* Event Card Styling */
.event-card {
  transition: all 0.2s ease;
  height: 100%;
  overflow: hidden;
}

.event-card.global-event-card {
  border-left: 3px solid #a855f7;
}

.event-card.school-event-card {
  border-left: 3px solid #10b981;
}

.event-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.event-card-content {
  background-color: white;
}

.event-month {
  font-size: 0.875rem;
  font-weight: 500;
  text-transform: uppercase;
}

.global-date .event-month {
  color: #7c3aed;
}

.school-date .event-month {
  color: #059669;
}

.event-day {
  font-size: 1.5rem;
  font-weight: 700;
}

.global-date .event-day {
  color: #6d28d9;
}

.school-date .event-day {
  color: #047857;
}

.event-details {
  flex: 1;
}

.event-title {
  font-weight: 600;
  font-size: 1rem;
  color: #1f2937;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.event-expanded-details {
  margin-top: 0.5rem;
  animation: slideDown 0.3s ease forwards;
  overflow: hidden;
}

@keyframes slideDown {
  from {
    max-height: 0;
    opacity: 0;
  }
  to {
    max-height: 500px;
    opacity: 1;
  }
}

.multi-day .event-days-range,
.date-range-compact {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
}

.from-day,
.to-day {
  font-weight: 600;
}

.range-separator {
  margin: 0 0.25rem;
  color: #9ca3af;
}

.date-range-compact {
  font-size: 0.75rem;
  flex-direction: column;
  text-align: center;
}

.from-date,
.to-date {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.from-month,
.to-month {
  font-size: 0.7rem;
  font-weight: 500;
}

.global-date .from-month,
.global-date .to-month {
  color: #7c3aed;
}

.school-date .from-month,
.school-date .to-month {
  color: #059669;
}

.from-day,
.to-day {
  font-size: 0.875rem;
  font-weight: 700;
}

.global-date .from-day,
.global-date .to-day {
  color: #6d28d9;
}

.school-date .from-day,
.school-date .to-day {
  color: #047857;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.events-dialog .p-dialog-content {
  padding: 1.5rem;
}

/* Event details sidebar styling */
.event-info-group {
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f3f4f6;
}

.event-info-group:last-child {
  border-bottom: none;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .event-scope-badge {
    font-size: 0.625rem;
    padding: 0.2rem 0.4rem;
  }

  .event-type-badge {
    width: 20px;
    height: 20px;
  }

  .event-scope-badge-small {
    width: 28px;
    height: 28px;
    font-size: 0.75rem;
  }
}
</style>
